import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import type { NormalizedComment } from "@common/types/issue";
import { relocatesBesideParagraph } from "@common/utils/redline/wire/redline-placement";

import { AnchorLog } from "../../../utils/anchor/anchor-log";
import type { AnchorTracker } from "../../../utils/anchor/anchor-tracker";
import { anchorTrackers, documentPages } from "../../../utils/anchor/anchor-tracker-registry";
import type { BookmarkPlacement, WordParagraph, WordSession } from "../../../utils/anchor/word-document";
import { timedBulkApplySpan } from "../../../utils/bulk-apply-run-timeline";
import type { BulkApplyCommentEntry } from "../../../utils/document";
import { ApplyFailureError } from "../../../utils/errors";
import { ProposalEngineAccess } from "../../../utils/proposal-engine-access";
import type { LivePageSnapshot } from "../proposal-tracker/page-revisions";
import type { CommentCardContent } from "./card-content";
import { redlineLocatingProposalCard } from "./locate-proposal-card";

type CommentTargetRecoveryState = "recovering" | "recovered" | "recoveryFoundNothing";

const COMMENT_TARGET_RECOVERY_STEP = "comment target recovery";

export const COMMENT_PARAGRAPH_GONE_MESSAGE = "Its paragraph is no longer in the document.";

export function commentParagraphIsPlacedBeside(commentBookmarks: readonly string[]): boolean {
  return relocatesBesideParagraph(anchorTrackers.placementOf(commentBookmarks));
}

const bookmarkKeyOf = (commentBookmarks: readonly string[]): string => [...commentBookmarks].sort().join("|");

export function issueCommentBookmarksOf(issueComment: NormalizedComment, issueDetectionBookmarks?: string[]): string[] {
  return issueComment.bookmark ? [issueComment.bookmark] : (issueDetectionBookmarks ?? []);
}

export function commentContentOfIssueComment(issueComment: NormalizedComment, commentBookmarks: string[]): CommentCardContent {
  return {
    kind: "comment",
    text: issueComment.text,
    bookmarks: commentBookmarks,
    anchorText: issueComment.anchorText,
    placement: issueComment.placement,
    paragraphText: issueComment.paragraphText ?? null,
    paragraphAnchor: issueComment.paragraphAnchor ?? null,
  };
}

export function commentContentOfBulkEntry(bulkCommentEntry: BulkApplyCommentEntry): CommentCardContent {
  return {
    kind: "comment",
    text: bulkCommentEntry.comment_text,
    bookmarks: bulkCommentEntry.target_bookmark_names,
    anchorText: bulkCommentEntry.anchor_text ?? null,
    placement: bulkCommentEntry.placement ?? null,
    paragraphText: null,
    paragraphAnchor: null,
  };
}

export function commentParagraphReadsGone(commentBookmarks: readonly string[], watchedPage: LivePageSnapshot): boolean {
  if (!watchedPage.completed) return false;
  const readCommentBookmarks = commentBookmarks.filter((commentBookmark) => watchedPage.coveredBookmarks.has(commentBookmark));
  return readCommentBookmarks.length > 0 && !readCommentBookmarks.some((commentBookmark) => watchedPage.presentBookmarks.has(commentBookmark));
}

class CommentTargetRecoveries {
  private readonly stateByBookmarkKey = new Map<string, CommentTargetRecoveryState>();
  private readonly listeners = new Set<() => void>();
  private recoveriesVersion = 0;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly version = (): number => this.recoveriesVersion;

  recoveryFoundNothing(commentBookmarks: readonly string[]): boolean {
    return this.stateByBookmarkKey.get(bookmarkKeyOf(commentBookmarks)) === "recoveryFoundNothing";
  }

  followTarget(commentContent: CommentCardContent, pageReadsTargetGone: boolean): void {
    const bookmarkKey = bookmarkKeyOf(commentContent.bookmarks);
    if (!pageReadsTargetGone) {
      if (this.stateByBookmarkKey.delete(bookmarkKey)) this.changed();
      return;
    }
    if (this.stateByBookmarkKey.has(bookmarkKey)) return;
    this.stateByBookmarkKey.set(bookmarkKey, "recovering");
    this.changed();
    void this.recover(commentContent, bookmarkKey);
  }

  private async recover(commentContent: CommentCardContent, bookmarkKey: string): Promise<void> {
    const found = await anchorTrackers
      .trackerFor(commentContent.bookmarks, redlineLocatingProposalCard(commentContent))
      .reconcileToFind("loss")
      .catch((error: unknown) => {
        AnchorLog.record({ kind: "error", bookmarkNames: commentContent.bookmarks, step: COMMENT_TARGET_RECOVERY_STEP, message: AnchorLog.messageOf(error) });
        return false;
      });
    this.stateByBookmarkKey.set(bookmarkKey, found ? "recovered" : "recoveryFoundNothing");
    if (found) documentPages.touch();
    this.changed();
  }

  private changed(): void {
    this.recoveriesVersion += 1;
    for (const listener of Array.from(this.listeners)) listener();
  }
}

export const commentTargetRecoveries = new CommentTargetRecoveries();

async function deleteCommentBookmarksHoldingNoWord(session: WordSession, commentBookmarks: readonly string[]): Promise<Set<string>> {
  const emptiedBookmarks = await session.bookmarks.namesHoldingNoWord([...commentBookmarks]);
  if (emptiedBookmarks.length > 0) await session.bookmarks.delete(emptiedBookmarks);
  return new Set(emptiedBookmarks);
}

export async function recoverCommentTargetWithin(context: Word.RequestContext, commentContent: CommentCardContent): Promise<void> {
  if (!(await ProposalEngineAccess.current()).enabled) return;
  await deleteCommentBookmarksHoldingNoWord(anchorTrackers.word.sessionFor(context), commentContent.bookmarks);
  try {
    await anchorTrackers.trackerFor(commentContent.bookmarks, redlineLocatingProposalCard(commentContent)).reconcileWithin(context, "apply");
  } catch (error) {
    AnchorLog.record({ kind: "error", bookmarkNames: commentContent.bookmarks, step: COMMENT_TARGET_RECOVERY_STEP, message: AnchorLog.messageOf(error) });
  }
  if (commentParagraphIsPlacedBeside(commentContent.bookmarks)) throw new ApplyFailureError(COMMENT_PARAGRAPH_GONE_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
}

interface CommentBookmarkStates {
  absent: ReadonlySet<string>;
  holdingNoWord: ReadonlySet<string>;
}

interface RecoveryTogether {
  placedTogether: CommentCardContent[];
  leftToReconcile: CommentCardContent[];
}

function commentLostItsBookmarks(commentBookmarks: readonly string[], bookmarkStates: CommentBookmarkStates): boolean {
  const holdsABookmarkWithNoWord = commentBookmarks.some((commentBookmark) => bookmarkStates.holdingNoWord.has(commentBookmark));
  const holdsNoBookmark = commentBookmarks.length > 0 && commentBookmarks.every((commentBookmark) => bookmarkStates.absent.has(commentBookmark));
  return holdsABookmarkWithNoWord || holdsNoBookmark;
}

function trackerOfComment(commentContent: CommentCardContent): AnchorTracker {
  return anchorTrackers.trackerFor(commentContent.bookmarks, redlineLocatingProposalCard(commentContent));
}

async function recoverTogetherFromOneParagraphList(session: WordSession, commentContentsNeedingRecovery: CommentCardContent[]): Promise<RecoveryTogether> {
  const everyCommentLeft: RecoveryTogether = { placedTogether: [], leftToReconcile: commentContentsNeedingRecovery };
  let listedParagraphs: WordParagraph[];
  try {
    listedParagraphs = await timedBulkApplySpan("comment_paragraph_list", () => session.listedParagraphs());
  } catch (error) {
    AnchorLog.record({ kind: "error", bookmarkNames: [], step: COMMENT_TARGET_RECOVERY_STEP, message: AnchorLog.messageOf(error) });
    return everyCommentLeft;
  }
  const bodyParagraphsById = new Map<string, WordParagraph>(listedParagraphs.map((listedParagraph) => [listedParagraph.uniqueLocalId, listedParagraph]));
  const paragraphsFoundByTracker = new Map<AnchorTracker, WordParagraph[]>();
  const commentContentsFoundByTracker = new Map<AnchorTracker, CommentCardContent[]>();
  const commentContentsNotFound: CommentCardContent[] = [];
  for (const commentContent of commentContentsNeedingRecovery) {
    const tracker = trackerOfComment(commentContent);
    const paragraphsFound = paragraphsFoundByTracker.get(tracker) ?? tracker.paragraphsFoundIn(bodyParagraphsById);
    if (paragraphsFound === null) {
      commentContentsNotFound.push(commentContent);
      continue;
    }
    paragraphsFoundByTracker.set(tracker, paragraphsFound);
    commentContentsFoundByTracker.set(tracker, [...(commentContentsFoundByTracker.get(tracker) ?? []), commentContent]);
  }
  const placements: BookmarkPlacement[] = Array.from(paragraphsFoundByTracker, ([tracker, paragraphs]) => ({ bookmarkNames: tracker.bookmarkNames, paragraphs }));
  if (placements.length === 0) return everyCommentLeft;
  try {
    await session.bookmarks.layOverTogether(placements);
  } catch (error) {
    AnchorLog.record({ kind: "error", bookmarkNames: placements.flatMap((placement) => placement.bookmarkNames), step: COMMENT_TARGET_RECOVERY_STEP, message: AnchorLog.messageOf(error) });
    return everyCommentLeft;
  }
  paragraphsFoundByTracker.forEach((_paragraphs, tracker) => tracker.settleOnParagraphsLaidOver());
  return { placedTogether: Array.from(commentContentsFoundByTracker.values()).flat(), leftToReconcile: commentContentsNotFound };
}

async function reconcileTogether(commentContents: CommentCardContent[]): Promise<Set<CommentCardContent>> {
  const recoveryFoundNothingFor = new Set<CommentCardContent>();
  await Promise.all(
    commentContents.map(async (commentContent) => {
      const found = await trackerOfComment(commentContent)
        .reconcileToFind("apply")
        .catch((error: unknown) => {
          AnchorLog.record({ kind: "error", bookmarkNames: commentContent.bookmarks, step: COMMENT_TARGET_RECOVERY_STEP, message: AnchorLog.messageOf(error) });
          return false;
        });
      if (!found) recoveryFoundNothingFor.add(commentContent);
    }),
  );
  return recoveryFoundNothingFor;
}

export async function recoverCommentTargetsForApply(commentContents: CommentCardContent[]): Promise<Set<CommentCardContent>> {
  if (!(await ProposalEngineAccess.current()).enabled) return new Set<CommentCardContent>();
  const everyCommentBookmark = [...new Set(commentContents.flatMap((commentContent) => commentContent.bookmarks))];
  if (everyCommentBookmark.length === 0) return new Set<CommentCardContent>();
  const spanDetail = { entries: commentContents.length, reconciled: 0, placedTogether: 0 };
  return timedBulkApplySpan(
    "comment_recovery_reconciled",
    async () => {
      const leftToReconcile = await anchorTrackers.word.withWrite(async (session) => {
        const { absent, holdingNoWord } = await timedBulkApplySpan("comment_bookmark_states", () => session.bookmarks.statesOf(everyCommentBookmark), { bookmarks: everyCommentBookmark.length });
        const bookmarkStates: CommentBookmarkStates = { absent: new Set(absent), holdingNoWord: new Set(holdingNoWord) };
        const commentContentsNeedingRecovery = commentContents.filter((commentContent) => commentLostItsBookmarks(commentContent.bookmarks, bookmarkStates));
        spanDetail.reconciled = commentContentsNeedingRecovery.length;
        if (commentContentsNeedingRecovery.length === 0) return [];
        const recoveredTogether = await recoverTogetherFromOneParagraphList(session, commentContentsNeedingRecovery);
        spanDetail.placedTogether = recoveredTogether.placedTogether.length;
        const emptiedBookmarksOfComments = new Set(recoveredTogether.leftToReconcile.flatMap((commentContent) => commentContent.bookmarks.filter((commentBookmark) => bookmarkStates.holdingNoWord.has(commentBookmark))));
        if (emptiedBookmarksOfComments.size > 0) await session.bookmarks.delete([...emptiedBookmarksOfComments]);
        return recoveredTogether.leftToReconcile;
      });
      return timedBulkApplySpan("comment_reconcile_together", () => reconcileTogether(leftToReconcile), { comments: leftToReconcile.length });
    },
    spanDetail,
  );
}
